import { cn } from "../../lib/cn";

interface SetupChecklistProps {
  hasPlantId: boolean;
  hasConnectors: boolean;
  hasSources: boolean;
  onNavigate: (path: string) => void;
}

export function SetupChecklist({
  hasPlantId,
  hasConnectors,
  hasSources,
  onNavigate,
}: Readonly<SetupChecklistProps>) {
  return (
    <div className="px-5 py-6">
      <div className="mb-5 rounded-lg border border-dashed border-rule bg-paper px-5 py-5 text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand-600">
          <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
            <path
              d="M10 4v12M4 10h12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <p className="text-[14px] font-semibold text-ink">Connect your first source</p>
        <p className="mt-1 text-[12.5px] text-ash">
          Browse available connectors and link a data source to start crawling.
        </p>
        <button onClick={() => onNavigate("/connectors")} className="btn-primary mt-4 text-[13px]">
          Browse connectors
        </button>
      </div>
      <ul className="space-y-2.5">
        <SetupStep
          done={hasPlantId}
          label="Set a Plant ID"
          action={() => onNavigate("/settings")}
          actionLabel="Settings →"
        />
        <SetupStep
          done={hasConnectors}
          label="Install at least one connector"
          action={() => onNavigate("/connectors")}
          actionLabel="Connectors →"
        />
        <SetupStep
          done={hasSources}
          label="Add a source"
          action={() => onNavigate("/sources")}
          actionLabel="Sources →"
        />
      </ul>
    </div>
  );
}

interface SetupStepProps {
  done: boolean;
  label: string;
  action: () => void;
  actionLabel: string;
}

function SetupStep({ done, label, action, actionLabel }: Readonly<SetupStepProps>) {
  return (
    <li className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
            done
              ? "border-emerald-500 bg-emerald-50 text-emerald-600"
              : "border-rule bg-paper text-ash",
          )}
        >
          {done ? (
            <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden>
              <path
                d="M2 6l3 3 5-5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <span className="h-1.5 w-1.5 rounded-full bg-rule" />
          )}
        </div>
        <span className={cn("text-[12.5px]", done ? "text-ash line-through" : "text-ink")}>
          {label}
        </span>
      </div>
      {!done && (
        <button
          onClick={action}
          className="shrink-0 text-[11.5px] font-medium text-brand-700 hover:underline"
        >
          {actionLabel}
        </button>
      )}
    </li>
  );
}
